// src/components/ERDAIGuideline.jsx
// Standalone page for editing the global ERD AI Guideline (rules sent to the LLM when generating feedback).
import React, { useState, useEffect } from 'react';
import { useClass } from '../utils/ClassContext';
import { apiCall } from '../utils/api/ApiUtils';

const ERDAIGuideline = ({ userRole }) => {
  const { getClassContext } = useClass();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const classContext = getClassContext();
    if (!classContext?.class_id) { setLoading(false); return; }
    setLoading(true);

    // Try component-scoped guideline first; fall back to root (legacy docs saved without component field)
    apiCall('/document-editor/ai-guideline?component=erd', { method: 'GET' }, classContext)
      .then(data => {
        if (data.guideline?.content) {
          setContent(data.guideline.content);
        } else {
          // Fallback: root-level guideline (no component filter)
          return apiCall('/document-editor/ai-guideline', { method: 'GET' }, classContext)
            .then(fb => setContent(fb.guideline?.content || ''));
        }
      })
      .catch(err => setError(`Failed to load guideline: ${err.message}`))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const classContext = getClassContext();
    if (!classContext?.class_id) return;
    setSaving(true);
    setError(null);
    try {
      await apiCall('/document-editor/ai-guideline', {
        method: 'POST',
        body: JSON.stringify({ component: 'erd', title: 'ERD AI Guideline', content }),
      }, classContext);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(`Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '32px 40px', maxWidth: 900, margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>ERD AI Guideline</h1>
        <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>
          Rules and instructions shipped to the LLM whenever it generates ERD feedback for your class.
          This applies to all environments — no need to set it per-environment.
          Supports plain text or Markdown.
        </p>
      </div>

      {/* Editor area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : (
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder={
              '# ERD Feedback Rules\n\n' +
              '- Always check cardinality notation carefully.\n' +
              '- Weak entities must have total participation in their identifying relationship.\n' +
              '- The strong entity does NOT need total participation.\n\n' +
              '# Additional Instructions\n\nAdd any other rules here...'
            }
            style={{
              flex: 1,
              width: '100%',
              minHeight: 480,
              fontFamily: 'monospace',
              fontSize: 13,
              lineHeight: 1.7,
              padding: '14px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              resize: 'vertical',
              boxSizing: 'border-box',
              color: '#1e293b',
              background: '#f8fafc',
              outline: 'none',
            }}
            onFocus={e => { e.target.style.border = '1px solid #6366f1'; e.target.style.background = '#fff'; }}
            onBlur={e => { e.target.style.border = '1px solid #e2e8f0'; e.target.style.background = '#f8fafc'; }}
          />
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          style={{
            padding: '10px 24px',
            background: saved ? '#22c55e' : '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: 14,
            transition: 'background 0.2s',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Guideline'}
        </button>
        {error && <span style={{ color: '#ef4444', fontSize: 13 }}>{error}</span>}
        {!error && content && (
          <span style={{ color: '#94a3b8', fontSize: 12 }}>
            {content.length} characters
          </span>
        )}
      </div>
    </div>
  );
};

export default ERDAIGuideline;
